import { useMarketoSessionLink } from '@/context/Marketo/hooks';
import { BrPage, BrPageContext } from '@bloomreach/react-sdk';
import { Configuration } from '@bloomreach/spa-sdk';
import axios from 'axios';
import { isEqual } from 'lodash';
import React, { memo, StrictMode, useContext, useEffect, useState } from 'react';
import { RouteComponentProps } from 'react-router-dom';
import useDeepCompareEffect from 'use-deep-compare-effect';
import mapping from './features/mapping';
import * as sharedComponents from './features/shared';
import { ErrorBoundary } from './features/shared';
import { useOutline } from './features/shared/Hooks';
import { useDataLayerPageLoad, useRegisterNavigationHistory } from './hooks';
import { PageProviders } from './providers/PageProviders';
import SingleColumn from './templates/SingleColumn';
import { BRXM_ENDPOINT, IS_PREVIEW, SPA_BASE_URL } from './utils/constants';
import PrerenderInterceptor from './utils/prerender-interceptor';

const Root = () => {
  const page: any = useContext(BrPageContext);
  const [isReady, setIsReady] = useState(false);

  // DON'T REMOVE THIS LINE
  // This is a workaround to start GTM after the page is ready
  useDataLayerPageLoad();

  useRegisterNavigationHistory();

  useOutline();

  // DON'T REMOVE THIS LINE
  // This is a workaround to link the session with Marketo
  useMarketoSessionLink();

  useEffect(() => {
    const reqEjectId = axios.interceptors.request.use(PrerenderInterceptor.requestHandler);
    const resEjectId = axios.interceptors.response.use(
      PrerenderInterceptor.responseHandler,
      PrerenderInterceptor.errorHandler,
    );

    return () => {
      axios.interceptors.request.eject(reqEjectId);
      axios.interceptors.response.eject(resEjectId);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useDeepCompareEffect(() => {
    window.scrollTo(0, 0);

    if (!isReady) {
      setIsReady(true);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page]);

  return (
    <StrictMode>
      <SingleColumn />
    </StrictMode>
  );
};

const Page = ({ location }: RouteComponentProps) => {
  const config: Configuration = {
    baseUrl: SPA_BASE_URL,
    endpoint: BRXM_ENDPOINT,
    httpClient: axios,
    path: `${location.pathname}${location.search}`,
    authorizationHeader: 'HST-Authorization',
    apiVersion: '1.0',
  };

  return (
    <ErrorBoundary>
      <BrPage configuration={config} mapping={mapping}>
        <PageProviders isPreview={IS_PREVIEW}>
          <Root />
          <sharedComponents.LangRedirectionByLocation />
        </PageProviders>
      </BrPage>
    </ErrorBoundary>
  );
};

export default memo(Page, isEqual);
